import type * as SignalR from '@microsoft/signalr'
import { Middleware } from '@reduxjs/toolkit'
import { MODAL_TYPE } from 'src/constants/modal.constants'
import {
  SIGNALR_BACKOFF_JITTER_RATIO,
  SIGNALR_HUB_RECONNECT_ATTEMPTS,
  SIGNALR_HUB_RECONNECT_BASE_DELAY_MS,
  SIGNALR_HUB_RECONNECT_MAX_DELAY_MS,
  SIGNALR_NEGOTIATE_BASE_DELAY_MS,
  SIGNALR_NEGOTIATE_MAX_DELAY_MS,
  SIGNALR_NEGOTIATE_MAX_RETRY_ATTEMPTS,
  SIGNALR_RESTART_BASE_DELAY_MS,
  SIGNALR_RESTART_MAX_DELAY_MS,
} from 'src/constants/signalR.constants'
import { getEnvVar, getEnvVarWithDefault } from 'src/lib/env'
import { ApprovalData } from 'src/types/plan.interface'
import {
  delay,
  getExponentialBackoffDelay,
} from 'src/utils/exponentialBackoff.util'

import { getAccessToken } from '../../lib/axiosInstance'
import { setAutoExtractedDataProcessed } from '../slice/createPlanSlice'
import {
  bulkExportCompleted,
  BulkExportCompleteEvent,
} from '../slice/exportedExcelFilesSlice'
import { setModal } from '../slice/modalSlice'
import {
  setMultiFamilyDraftPlanDataUpToDate,
  setMultiFamilySubmittedPlanDataUpToDate,
} from '../slice/MultiFamilyPlanListingSlice'
import {
  setSingleFamilyDraftPlanDataUpToDate,
  setSingleFamilySubmittedPlanDataUpToDate,
} from '../slice/plansSlice'
import {
  setConnectionStatus,
  signalRApprovalMessageReceived,
} from '../slice/signalRSlice'
// Type-only: the store builds this middleware, so a runtime import would be circular.
import type { RootState } from '../stores/store'

const HTTP_METHOD_POST = 'POST'

const NEGOTIATE_BACKOFF_OPTIONS = {
  baseDelayMs: SIGNALR_NEGOTIATE_BASE_DELAY_MS,
  jitterRatio: SIGNALR_BACKOFF_JITTER_RATIO,
  maxDelayMs: SIGNALR_NEGOTIATE_MAX_DELAY_MS,
}

const HUB_RECONNECT_BACKOFF_OPTIONS = {
  baseDelayMs: SIGNALR_HUB_RECONNECT_BASE_DELAY_MS,
  jitterRatio: SIGNALR_BACKOFF_JITTER_RATIO,
  maxDelayMs: SIGNALR_HUB_RECONNECT_MAX_DELAY_MS,
}

const RESTART_BACKOFF_OPTIONS = {
  baseDelayMs: SIGNALR_RESTART_BASE_DELAY_MS,
  jitterRatio: SIGNALR_BACKOFF_JITTER_RATIO,
  maxDelayMs: SIGNALR_RESTART_MAX_DELAY_MS,
}

// The client's default policy ramps 0s, 2s, 10s, 30s and then gives up for
// good. Ramping exponentially buys more time for a longer outage, and stopping
// after a bounded number of tries is deliberate: it lets `onclose` hand over to
// the middleware's restart loop, which re-negotiates and so can recover from an
// expired access token that this policy never can.
export const hubReconnectPolicy: SignalR.IRetryPolicy = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
    previousRetryCount >= SIGNALR_HUB_RECONNECT_ATTEMPTS
      ? null
      : getExponentialBackoffDelay(
          previousRetryCount,
          HUB_RECONNECT_BACKOFF_OPTIONS
        ),
}

interface ExtractedPlanSignalREvent {
  error?: string
  failedPlanIds?: string[]
  fileUploadedBy?: string
  planIds?: string[]
  successfullyUpdatedDraftPlan: boolean
  successfulPlans?: string[]
}

export const signalRMiddleware: Middleware = (store) => {
  let connection: SignalR.HubConnection | null = null
  // Handlers registered on a hub connection can't be unregistered, so every
  // connection attempt takes the next generation number and its callbacks
  // ignore events once a newer attempt — or an explicit stop — supersedes them.
  // Without this, a superseded socket's `onclose` would schedule a reconnect
  // the app never asked for.
  let connectionGeneration = 0
  let restartAttempt = 0
  let restartTimer: ReturnType<typeof setTimeout> | null = null

  const createHeaders = (accessToken: string): Headers => {
    const headers = new Headers()
    headers.append(
      'Ocp-Apim-Subscription-Key',
      getEnvVarWithDefault('REACT_APP_SIGNALR_APIM_KEY', '')
    )
    headers.append('Msal-Authorization', `Bearer ${accessToken}`)
    return headers
  }

  const negotiateConnection = async (): Promise<{
    accessToken: string
    resJson: { accessToken: string; url: string }
  }> => {
    let accessToken = await getAccessToken()
    const environmentUrl =
      getEnvVar('REACT_APP_ENVIRONMENT')?.toLowerCase() === 'prod'
        ? ''
        : `-${getEnvVar('REACT_APP_ENVIRONMENT')}`

    // The negotiate endpoint lives in the plan-change-feed function app, which
    // is fronted by APIM when deployed. Running that app locally means the
    // deployed URL would still be negotiated against — and the local change
    // feed's SignalR messages would never reach this client — so an explicit
    // override wins when one is configured.
    const negotiateUrl = getEnvVarWithDefault(
      'REACT_APP_SIGNALR_NEGOTIATE_URL',
      `https://plr${environmentUrl}.lennar.com/api/negotiate`
    )

    for (
      let attempt = 0;
      attempt <= SIGNALR_NEGOTIATE_MAX_RETRY_ATTEMPTS;
      attempt++
    ) {
      const headers = createHeaders(accessToken)

      const response = await fetch(negotiateUrl, {
        headers,
        method: HTTP_METHOD_POST,
      })

      if (response.ok) {
        const resJson = await response.json()
        return {
          accessToken,
          resJson,
        }
      }

      if (attempt === SIGNALR_NEGOTIATE_MAX_RETRY_ATTEMPTS) {
        throw new Error(
          `Failed to negotiate SignalR connection after ${
            SIGNALR_NEGOTIATE_MAX_RETRY_ATTEMPTS + 1
          } attempts`
        )
      }

      // Back off before asking again. An immediate retry loop only adds load
      // to an endpoint that is already failing, and gives a token rejected for
      // clock skew no time to become valid.
      await delay(
        getExponentialBackoffDelay(attempt, NEGOTIATE_BACKOFF_OPTIONS)
      )
      accessToken = await getAccessToken()
    }

    // Unreachable: the final attempt throws above, but TypeScript can't see it.
    throw new Error('Unexpected error in negotiateConnection')
  }

  const clearPendingRestart = (): void => {
    if (restartTimer !== null) {
      clearTimeout(restartTimer)
      restartTimer = null
    }
  }

  const registerEventHandlers = (
    hubConnection: SignalR.HubConnection
  ): void => {
    hubConnection.on('versionChangeMessage', () => {
      store.dispatch(setModal({ property: MODAL_TYPE.NEW_VERSION, value: {} }))
    })

    hubConnection.on('approvalMessage', (document: ApprovalData) => {
      store.dispatch(signalRApprovalMessageReceived(document))
    })

    hubConnection.on('submittedPlanChangeMessage', (planChange: boolean) => {
      store.dispatch(setSingleFamilySubmittedPlanDataUpToDate(!planChange))
    })

    hubConnection.on('draftPlanChangeMessage', (planChange: boolean) => {
      store.dispatch(setSingleFamilyDraftPlanDataUpToDate(!planChange))
    })

    hubConnection.on(
      'submittedMultiFamilyPlanChangeMessage',
      (planChange: boolean) => {
        store.dispatch(setMultiFamilySubmittedPlanDataUpToDate(!planChange))
      }
    )

    hubConnection.on(
      'draftMultiFamilyPlanChangeMessage',
      (planChange: boolean) => {
        store.dispatch(setMultiFamilyDraftPlanDataUpToDate(!planChange))
      }
    )

    // Bulk exports are queued rather than awaited, so this is how the app
    // learns a workbook is ready. The hub broadcasts to every client, so
    // drop events belonging to somebody else's export.
    hubConnection.on(
      'bulkExportCompleteMessage',
      (signalREvent: BulkExportCompleteEvent) => {
        const { userUuid } = store.getState() as RootState
        if (signalREvent.userUuid !== userUuid?.userUuid) return

        store.dispatch(bulkExportCompleted(signalREvent))
      }
    )

    hubConnection.on(
      'autoExtractForDraftPlanChangeMessage',
      (signalREvent: ExtractedPlanSignalREvent) => {
        store.dispatch(
          setAutoExtractedDataProcessed({
            error: signalREvent.error || null,
            failedPlanIds: signalREvent.failedPlanIds || [],
            fileUploadedBy: signalREvent.fileUploadedBy || '',
            planIds: signalREvent.planIds || [],
            successfullyUpdatedDraftPlan:
              signalREvent.successfullyUpdatedDraftPlan,
            successfulPlans: signalREvent.successfulPlans || [],
          })
        )
      }
    )
  }

  const openConnection = async (): Promise<void> => {
    connectionGeneration += 1
    const generation = connectionGeneration
    const isCurrentGeneration = (): boolean =>
      generation === connectionGeneration

    clearPendingRestart()
    store.dispatch(setConnectionStatus('connecting'))

    try {
      // A start that lands on top of an existing connection (StrictMode's
      // double-invoked effect, or a manual start racing a scheduled restart)
      // must not leave the previous socket open behind it.
      const supersededConnection = connection
      connection = null
      if (supersededConnection) {
        await supersededConnection.stop()
      }

      // Load the signalR client on demand so its bundle stays out of the
      // main entry chunk until a realtime connection is actually started.
      const signalR = await import('@microsoft/signalr')

      const { resJson } = await negotiateConnection()
      if (!isCurrentGeneration()) return

      const hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(resJson.url, {
          accessTokenFactory: () => resJson.accessToken,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect(hubReconnectPolicy)
        .build()

      registerEventHandlers(hubConnection)

      hubConnection.onreconnecting(() => {
        if (!isCurrentGeneration()) return
        store.dispatch(setConnectionStatus('connecting'))
      })

      hubConnection.onreconnected(() => {
        if (!isCurrentGeneration()) return
        restartAttempt = 0
        store.dispatch(setConnectionStatus('connected'))
      })

      // Reached once the client has exhausted `hubReconnectPolicy`, or when
      // the server closes the socket outright. The negotiated access token may
      // be expired by this point, so recover by negotiating a new connection
      // rather than retrying this one.
      hubConnection.onclose(() => {
        if (!isCurrentGeneration()) return
        connection = null
        store.dispatch(setConnectionStatus('disconnected'))
        scheduleRestart()
      })

      connection = hubConnection
      await hubConnection.start()

      // Stopped while the handshake was still in flight.
      if (!isCurrentGeneration()) {
        await hubConnection.stop()
        return
      }

      restartAttempt = 0
      store.dispatch(setConnectionStatus('connected'))
    } catch {
      if (!isCurrentGeneration()) return
      connection = null
      store.dispatch(setConnectionStatus('disconnected'))
      scheduleRestart()
    }
  }

  const scheduleRestart = (): void => {
    clearPendingRestart()

    const waitMs = getExponentialBackoffDelay(
      restartAttempt,
      RESTART_BACKOFF_OPTIONS
    )
    restartAttempt += 1

    restartTimer = setTimeout(() => {
      restartTimer = null
      // openConnection reports its own failures through the connection status
      // and schedules the next attempt itself, so this catch only stops a
      // surprise from surfacing as an unhandled rejection.
      openConnection().catch(() =>
        store.dispatch(setConnectionStatus('disconnected'))
      )
    }, waitMs)
  }

  const closeConnection = async (): Promise<void> => {
    // Supersede any in-flight attempt and cancel a pending restart, so a
    // teardown can't be undone seconds later by a timer nobody stopped.
    connectionGeneration += 1
    clearPendingRestart()
    restartAttempt = 0

    const openHubConnection = connection
    connection = null
    if (openHubConnection) {
      await openHubConnection.stop()
    }

    store.dispatch(setConnectionStatus('disconnected'))
  }

  return (next) => async (action) => {
    if (action.type === 'signalR/startConnection') {
      await openConnection()

      return next(action)
    }

    if (action.type === 'signalR/stopConnection') {
      await closeConnection()
    }

    return next(action)
  }
}
